import React, { useContext, useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axios';

export default function Assistant() {
  const { auth } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // input state
  const [mobileText, setMobileText] = useState('');
  const [mobileFile, setMobileFile] = useState(null);
  const [desktopText, setDesktopText] = useState('');
  const [desktopFile, setDesktopFile] = useState(null);
  const [showDesktopMenu, setShowDesktopMenu] = useState(false);

  const fileInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const desktopInputRef = useRef(null);

  // initial message
  useEffect(() => {
    setMessages([
      {
        sender: 'bot',
        text: `Hello, **${auth?.username || 'Guest'}**! 👋\n\nHow can I help you today with budgeting, saving, or investing?`
      }
    ]);
  }, [auth]);

  // auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // refocus
  useEffect(() => {
    if (!loading) {
      if (window.innerWidth < 768 && mobileInputRef.current) {
        mobileInputRef.current.focus();
      } else if (desktopInputRef.current) {
        desktopInputRef.current.focus();
      }
    }
  }, [loading]);

  const handleSend = async ({ text, file }) => {
    if (file) {
      setMessages(ms => [...ms, { sender: 'user', text: `Uploaded: **${file.name}**` }]);
    }
    if (text) {
      setMessages(ms => [...ms, { sender: 'user', text }]);
    }

    setLoading(true);
    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        if (text) formData.append('message', text);
        res = await axios.post('/assistant/csv/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await axios.post('/assistant/', { message: text });
      }
      setMessages(ms => [...ms, { sender: 'bot', text: res.data.response }]);
    } catch (err) {
      console.error('Chatbot error:', err);
      setMessages(ms => [
        ...ms,
        {
          sender: 'bot',
          text: '😞 Oops! Emmanuel is having a hard time responding. Please try again later.'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // mobile helpers
  const onMobilePickFile = (e) => {
    const f = e.target.files?.[0] ?? null;
    setMobileFile(f);
  };
  const onMobilePlusClick = () => {
    fileInputRef.current?.click();
  };
  const onMobileSend = async () => {
    if (loading) return;
    if (!mobileText && !mobileFile) return;
    const textToSend = mobileText.trim();
    setMobileText('');
    await handleSend({ text: textToSend, file: mobileFile });
    setMobileFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // desktop helpers
  const onDesktopPickFile = (e) => {
    const f = e.target.files?.[0] ?? null;
    setDesktopFile(f);
    setShowDesktopMenu(false);
  };
  const onDesktopSend = async () => {
    if (loading) return;
    if (!desktopText && !desktopFile) return;
    const textToSend = desktopText.trim();
    setDesktopText('');
    await handleSend({ text: textToSend, file: desktopFile });
    setDesktopFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="flex justify-center bg-transparent md:bg-[#e4e6e7] h-[calc(100vh-4rem)]">
      <div className="w-full max-w-7xl bg-[#1e1e1e] text-white md:rounded-2xl rounded-none shadow-xl flex flex-col">
        
        {/* Chat messages */}
        <div
          className="flex-1 overflow-y-auto px-6 py-4 no-scrollbar"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 100px)' }}
        >
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex items-start gap-3 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.sender === 'bot' && (
                <div className="w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  E
                </div>
              )}
              <div
                className={`max-w-[75%] px-4 py-3 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-[#2d2d2d] text-white'
                }`}
              >
                <ReactMarkdown>{msg.text}</ReactMarkdown>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-start gap-3 justify-start">
              <div className="w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                E
              </div>
              <div className="bg-[#2d2d2d] text-gray-300 px-4 py-2 rounded-lg text-sm animate-pulse">
                Emmanuel is typing...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* ---------- Mobile input ---------- */}
        <div
          className="md:hidden border-t border-gray-800 px-3 py-3 bg-[#1e1e1e]"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 12px)' }}
        >
          {mobileFile && (
            <div className="mb-2 px-3 py-2 bg-gray-800 rounded-md text-xs flex items-center justify-between">
              <div className="truncate mr-2">{mobileFile.name}</div>
              <button
                className="text-xs text-gray-300 hover:text-white"
                onClick={() => {
                  setMobileFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                aria-label="Remove file"
              >
                ✕
              </button>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={onMobilePlusClick}
              disabled={loading}
              className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm ${
                loading ? 'bg-gray-700 text-gray-400' : 'bg-gray-800 text-white'
              }`}
              aria-label="Attach"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv, .xlsx, .xls"
              className="hidden"
              onChange={onMobilePickFile}
              disabled={loading}
            />

            <div className="flex-1">
              <input
                ref={mobileInputRef}
                type="text"
                value={mobileText}
                onChange={(e) => setMobileText(e.target.value)}
                placeholder="Ask Emmanuel anything"
                className="w-full bg-gray-900 text-white placeholder-gray-400 rounded-full py-3 px-4 focus:outline-none focus:ring-0 text-sm"
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onMobileSend();
                  }
                }}
              />
            </div>

            <button
              onClick={onMobileSend}
              disabled={loading}
              className={`ml-2 w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${
                loading ? 'bg-gray-600 cursor-not-allowed' : 'bg-cyan-500'
              }`}
              aria-label="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13"></path>
                <path d="M22 2L15 22l-4-9-9-4 18-7z"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* ---------- Desktop input ---------- */}
        <div className="hidden md:block border-t border-gray-800 bg-[#1e1e1e] px-4 py-3">
          {desktopFile && (
            <div className="mb-2 px-3 py-2 bg-gray-800 rounded-md text-xs flex items-center justify-between">
              <div className="truncate mr-2">{desktopFile.name}</div>
              <button
                className="text-xs text-gray-300 hover:text-white"
                onClick={() => {
                  setDesktopFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                aria-label="Remove file"
              >
                ✕
              </button>
            </div>
          )}

          <div className="flex items-center gap-3 relative">
            {/* Plus button */}
            <button
              onClick={() => setShowDesktopMenu(!showDesktopMenu)}
              disabled={loading}
              className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm ${
                loading ? 'bg-gray-700 text-gray-400' : 'bg-gray-800 text-white'
              }`}
              aria-label="Menu"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {showDesktopMenu && (
              <div className="absolute bottom-14 left-0 bg-[#2d2d2d] rounded-md shadow-lg p-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="block px-4 py-2 text-sm text-white hover:bg-gray-700 rounded"
                >
                  Attach CSV
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv, .xlsx, .xls"
              className="hidden"
              onChange={onDesktopPickFile}
              disabled={loading}
            />

            {/* Input */}
            <div className="flex-1">
              <input
                ref={desktopInputRef}
                type="text"
                value={desktopText}
                onChange={(e) => setDesktopText(e.target.value)}
                placeholder="Ask Emmanuel anything"
                className="w-full bg-gray-900 text-white placeholder-gray-400 rounded-full py-3 px-4 focus:outline-none focus:ring-0 text-sm"
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onDesktopSend();
                  }
                }}
              />
            </div>

            {/* Send button */}
            <button
              onClick={onDesktopSend}
              disabled={loading}
              className={`ml-2 w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${
                loading ? 'bg-gray-600 cursor-not-allowed' : 'bg-cyan-500'
              }`}
              aria-label="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13"></path>
                <path d="M22 2L15 22l-4-9-9-4 18-7z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
